/**
 * Keeps a pinch on the map zooming the map, not the page.
 *
 * MapLibre sets `touch-action: none` on its canvas container, which reads like it
 * should be enough — but WebKit gates viewport scaling on the viewport meta tag
 * alone and ignores `touch-action` for it. So a pinch drives MapLibre's own handler
 * and the browser's page zoom at once, and the layout is fixed-viewport
 * (`html`/`body` are `overflow: hidden`, the map is `100dvh`), so the zoomed page
 * has nowhere to scroll and the user is left stranded.
 *
 * Page zoom is switched off in two places, because neither covers every surface:
 *
 *  - `maximum-scale=1, user-scalable=no` in public/index.html handles WKWebView (the
 *    native shell on iOS), Android WebView and Android browsers.
 *  - iOS Safari has ignored `user-scalable=no` since iOS 10, so for anyone opening
 *    the web app in a browser instead of through the app we also swallow WebKit's
 *    non-standard `gesture*` events, which is how Safari drives pinch zoom. They
 *    exist only on WebKit; listening for them elsewhere is inert.
 *
 * Double-tap zoom is handled separately, by `touch-action: manipulation` on the body
 * in src/index.less.
 */

const GESTURE_EVENTS = ['gesturestart', 'gesturechange', 'gestureend'];

const preventGesture = (event) => {
	event.preventDefault();
};

/**
 * Disable browser page zoom for the lifetime of the app.
 * @returns {() => void} teardown
 */
export function disablePageZoom() {
	// `passive: false` is required — listeners on these are passive by default in
	// some engines, and a passive listener cannot preventDefault.
	GESTURE_EVENTS.forEach((name) => {
		document.addEventListener(name, preventGesture, { passive: false });
	});

	return () => {
		GESTURE_EVENTS.forEach((name) => {
			document.removeEventListener(name, preventGesture);
		});
	};
}

const pageZoom = { disablePageZoom };

export default pageZoom;
